import React, { useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import KpiRow from '../shared/KpiRow';
import KpiTile from '../shared/KpiTile';
import ChartPanel from '../shared/ChartPanel';
import DateRangePicker from '../shared/DateRangePicker';
import DistributionBar from '../shared/DistributionBar';
import AnalysisTable from '../shared/AnalysisTable';
import ComparisonSelector from '../shared/ComparisonSelector';
import { useAnalysisRange } from '../../../hooks/analysis/useAnalysisRange';
import { usePaymentAnalysis } from '../../../hooks/analysis/usePaymentAnalysis';
import { Granularity } from '../../../utils/analytics/types';
import { ComparisonMode, COMPARISON_NOTE } from '../../../utils/analytics/comparison';
import { formatCurrency, formatPercentage } from '../../../utils/formatters';
import { formatDate } from '../../../utils/dateUtils';
import { AXIS_PROPS, CHART_INK, SERIES_COLORS } from '../shared/chartTheme';

const PaymentAnalysis: React.FC = () => {
  const { range, setRange } = useAnalysisRange();
  const [granularity, setGranularity] = useState<Granularity>('week');
  const [comparison, setComparison] = useState<ComparisonMode>('none');
  const data = usePaymentAnalysis(range, granularity, comparison);
  const compareNote = comparison !== 'none' ? COMPARISON_NOTE[comparison] : undefined;

  return (
    <div>
      <div className="analysis-controls">
        <DateRangePicker value={range} onChange={setRange} />
        <ComparisonSelector value={comparison} onChange={setComparison} />
        <div className="analysis-toggle-group" role="group" aria-label="Granularity">
          {(['day', 'week', 'month'] as Granularity[]).map(g => (
            <button key={g} className={granularity === g ? 'active' : ''} onClick={() => setGranularity(g)}>
              {g.charAt(0).toUpperCase() + g.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <KpiRow>
        <KpiTile
          label="Collected"
          value={formatCurrency(data.collectedTotal)}
          delta={data.deltas?.collected}
          note={compareNote ?? 'successful payments'}
        />
        <KpiTile label="Refunded" value={formatCurrency(data.refundTotal)} delta={data.deltas?.refunds} note={compareNote} />
        <KpiTile
          label="Failure rate"
          value={data.failureRate !== null ? formatPercentage(data.failureRate) : '—'}
          delta={data.deltas?.failureRate}
          note={compareNote ?? `${data.failedCount} failed`}
        />
        <KpiTile
          label="Median time to paid"
          value={data.medianHoursToPaid !== null ? `${data.medianHoursToPaid} h` : '—'}
          note="booking → first payment"
        />
        <KpiTile
          label="Balance due"
          value={formatCurrency(data.outstanding.reduce((acc, r) => acc + r.balance, 0))}
          note={`${data.outstanding.length} reservations`}
        />
      </KpiRow>

      <ChartPanel
        title="Payments collected"
        loading={data.loading}
        error={data.error}
        empty={data.collected.every(p => p.value === 0)}
        table={{
          columns: [
            { key: 'label', label: 'Period' },
            { key: 'value', label: 'Collected', align: 'right', format: formatCurrency },
          ],
          rows: data.collected,
        }}
      >
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={data.collected} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
            <CartesianGrid stroke={CHART_INK.grid} vertical={false} />
            <XAxis dataKey="label" {...AXIS_PROPS} minTickGap={24} />
            <YAxis {...AXIS_PROPS} tickFormatter={v => `$${Number(v).toLocaleString()}`} width={70} />
            <Tooltip formatter={(value: any) => [formatCurrency(Number(value)), 'Collected']} />
            <Line type="monotone" dataKey="value" stroke={SERIES_COLORS[0]} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </ChartPanel>

      <ChartPanel
        title="Refunds"
        subtitle="Same time scale as payments collected"
        loading={data.loading}
        error={data.error}
        empty={data.refunds.every(p => p.value === 0)}
        emptyMessage="No refunds in this range."
        table={{
          columns: [
            { key: 'label', label: 'Period' },
            { key: 'value', label: 'Refunded', align: 'right', format: formatCurrency },
          ],
          rows: data.refunds,
        }}
      >
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={data.refunds} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
            <CartesianGrid stroke={CHART_INK.grid} vertical={false} />
            <XAxis dataKey="label" {...AXIS_PROPS} minTickGap={24} />
            <YAxis {...AXIS_PROPS} tickFormatter={v => `$${Number(v).toLocaleString()}`} width={70} />
            <Tooltip formatter={(value: any) => [formatCurrency(Number(value)), 'Refunded']} />
            <Line type="monotone" dataKey="value" stroke={SERIES_COLORS[5]} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </ChartPanel>

      <div className="chart-panel-grid">
        <ChartPanel
          title="Failed payments by error"
          loading={data.loading}
          error={data.error}
          empty={data.byErrorCode.length === 0}
          emptyMessage="No failed payments in this range. 🎉"
        >
          <AnalysisTable
            columns={[
              { key: 'label', label: 'Error code' },
              { key: 'count', label: 'Count', align: 'right' },
              { key: 'amount', label: 'Amount', align: 'right', format: formatCurrency },
            ]}
            rows={data.byErrorCode}
            defaultSortKey="count"
          />
        </ChartPanel>

        <ChartPanel
          title="Failed payments by gateway"
          loading={data.loading}
          error={data.error}
          empty={data.byGateway.length === 0}
          emptyMessage="No failed payments in this range. 🎉"
        >
          <AnalysisTable
            columns={[
              { key: 'label', label: 'Gateway' },
              { key: 'count', label: 'Count', align: 'right' },
              { key: 'amount', label: 'Amount', align: 'right', format: formatCurrency },
            ]}
            rows={data.byGateway}
            defaultSortKey="count"
          />
        </ChartPanel>
      </div>

      <ChartPanel
        title="Payment method mix"
        subtitle="Successful payments by card brand / method"
        loading={data.loading}
        error={data.error}
        empty={data.mix.length === 0}
      >
        <DistributionBar
          segments={data.mix.map(row => ({
            label: row.label,
            value: row.amount,
            display: formatCurrency(row.amount),
          }))}
        />
      </ChartPanel>

      <ChartPanel
        title="Outstanding balances"
        subtitle="Reservation cost minus successful payments (net of refunds)"
        loading={data.loading}
        error={data.error}
        empty={data.outstanding.length === 0}
        emptyMessage="Every reservation is fully paid. 🎉"
      >
        <AnalysisTable
          columns={[
            { key: 'confirmationCode', label: 'Confirmation' },
            { key: 'checkIn', label: 'Check-in', format: v => formatDate(String(v)) },
            { key: 'cost', label: 'Cost', align: 'right', format: formatCurrency },
            { key: 'paid', label: 'Paid', align: 'right', format: formatCurrency },
            { key: 'balance', label: 'Balance', align: 'right', format: formatCurrency },
          ]}
          rows={data.outstanding}
          defaultSortKey="balance"
          maxRows={25}
        />
      </ChartPanel>
    </div>
  );
};

export default PaymentAnalysis;
